/* global plotline */
import { captureMessage } from "@sentry/react";
import { useNavigate } from "react-router-dom";
import { getPlatform } from "utils/config";

const usePlotline = () => {
  const apiKey = process.env.REACT_APP_PLOTLINE_API_KEY;
  const plotlineEnabled = !localStorage.adminToken; // disable for admin login

  const navigate = useNavigate();

  const initPlotline = (userId, handlePlotlineEventsData, userContext = {}) => {
    if (apiKey && plotlineEnabled) {
      try {
        plotline("init", apiKey, userId);

        // Plotline expects an ISO 639-1 code (e.g. "hi", "en") for multilingual
        // campaigns. The WebView inherits navigator.language from the device OS,
        // so this routes the user's device language into Plotline. The
        // typeof check guards against environments where `navigator` is
        // undefined (e.g. SSR or non-browser test runners).
        const navLanguage =
          typeof navigator !== "undefined" ? navigator.language : null;
        const localeForPlotline = (navLanguage || "en")
          .split("-")[0]
          .toLowerCase();
        plotline("setLocale", localeForPlotline);

        // Log to Sentry so we can verify in production what locale the WebView
        // exposed and what we sent to Plotline. We only log non-English locales
        // because (a) those are the cases we actually need to verify for
        // multilingual campaigns, and (b) it keeps Sentry event volume low.
        // No PII is sent — only creatorId (numeric internal ID) for correlation.
        // Filter in Sentry with `tag:feature:plotline-locale`.
        if (localeForPlotline !== "en") {
          try {
            captureMessage("Plotline locale set", {
              level: "info",
              tags: {
                feature: "plotline-locale",
                platform: getPlatform(),
                localeSent: localeForPlotline,
              },
              extra: {
                creatorId: userContext.creatorId,
                creatorUsername: userContext.creatorUsername,
                navigatorLanguage: navigator.language,
                navigatorLanguages: navigator.languages,
                resolvedLocale: Intl.DateTimeFormat().resolvedOptions().locale,
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                localeSentToPlotline: localeForPlotline,
              },
            });
          } catch (sentryError) {
            console.error("Plotline locale Sentry log failed:", sentryError);
          }
        }

        plotline("setPlotlineEventsListener", (eventName, properties) => {
          handlePlotlineEventsData(eventName, properties);
        });
        plotline("setRedirectListener", (json) => {
          if (json && json["NAVIGATE"]) {
            navigate(json["NAVIGATE"]);
          }
        });
        trackEventPlotline("session_start", {
          platform: getPlatform(),
        });
      } catch (error) {
        console.log(error);
      }
    }
  };

  const trackEventPlotline = (eventName, params = {}) => {
    if (apiKey && plotlineEnabled) {
      try {
        plotline("track", eventName, params);
      } catch (error) {
        console.error("Plotline track event error:", error);
      }
    }
  };

  const setUserPropertiesPlotline = (params) => {
    if (!params || typeof params !== "object") return;
    if (apiKey && plotlineEnabled) {
      try {
        const updatedParams = Object.fromEntries(
          Object.entries(params)?.map(([key, value]) => [key, String(value)])
        );
        plotline("identify", updatedParams);
      } catch (error) {
        console.error("Plotline user properties error:", error);
      }
    }
  };

  return {
    initPlotline,
    trackEventPlotline,
    setUserPropertiesPlotline,
  };
};

export default usePlotline;
